import React, { useCallback, useMemo } from "react";

import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";

import useMyFavorites from "../../Apollo/hooks/useMyFavorites";
import { useAuth } from "@/auth";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { ACCOUNT_FAVORITES_ROUTE } from "@/common/static/routes.static";
import { isFavoriteItemProduct } from "@/common/utils/type-guards.utils";
import { ProductCard } from "@/features/search/components/product-card/ProductCard";
import { toCardProduct } from "@/features/search/utils/gateway-product.utils";
import { Heading } from "@whoppah/ui/cva/Heading";

type ProductDetailsFavoriteItemsProps = {
  className?: string;
};

export const ProductDetailsFavoriteItems = ({
  className,
}: ProductDetailsFavoriteItemsProps) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();

  const { data, loading } = useMyFavorites({
    skip: !isLoggedIn,
    variables: { limit: 4 },
  });

  const products = useMemo(() => {
    if (!data?.favorites?.items) {
      return [];
    }

    const mappedFavoriteItems = data.favorites.items.map(item => item.item);
    const filteredFavoriteItemProducts = mappedFavoriteItems.filter(
      isFavoriteItemProduct
    );

    return filteredFavoriteItemProducts;
  }, [data?.favorites?.items]);

  /** Track `view_item_list` event. */
  analyticsService.useExecAfterMount(
    useCallback(
      () => {
        if (!loading) {
          analyticsService.trackViewItemList(
            "pdp_favorites",
            "pdp_favorites",
            products
          );
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [loading, products.map(p => p.id).join(",")]
    )
  );

  if (products.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <div className="flex flex-col space-y-4">
        <Heading as="h4" className="mx-auto">
          {t("pdp:pdp-user-favorites")}
        </Heading>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
          {products.map(product => (
            <ProductCard key={product.id} product={toCardProduct(product)} />
          ))}
        </div>
        <LinkButton
          href={ACCOUNT_FAVORITES_ROUTE}
          appearance="minimal"
          className="self-center"
        >
          {t("pdp:pdp-see-all-your-favorites")}
        </LinkButton>
      </div>
    </div>
  );
};
